import type { NodeAnimationStep } from '@api/types.gen'

/**
 * The Web Animations keyframes behind the `rule` trigger, kept free of both DOM
 * access and Vue so the box arithmetic below can be unit-tested. Everything here
 * is expressed as the pair [hidden, shown]; the leave plays it reversed.
 */

export const DEFAULT_IN_DURATION = 300
export const DEFAULT_OUT_DURATION = 200
export const DEFAULT_IN_EASING = 'ease-out'
export const DEFAULT_OUT_EASING = 'ease-in'
export const DEFAULT_DISTANCE = 16
export const DEFAULT_SCALE = 0.96

export const COLLAPSE_TYPES: ReadonlySet<string> = new Set(['collapse', 'collapse-fade'])

/** `direction` is where the element travels TO, so it starts from the opposite side. */
export function slideOffset(step: NodeAnimationStep): { dx: number; dy: number } {
  const distance = step.distance ?? DEFAULT_DISTANCE
  switch (step.direction) {
    case 'down':
      return { dx: 0, dy: -distance }
    case 'left':
      return { dx: distance, dy: 0 }
    case 'right':
      return { dx: -distance, dy: 0 }
    case 'up':
    default:
      return { dx: 0, dy: distance }
  }
}

/** The subset of an element's computed box a collapse has to animate. */
export interface BoxMetrics {
  /** Border-box height, as `getBoundingClientRect().height` reports it. */
  outerHeight: number
  boxSizing: string
  paddingTop: string
  paddingBottom: string
  marginTop: string
  marginBottom: string
  borderTopWidth: string
  borderBottomWidth: string
}

const px = (value: string) => parseFloat(value) || 0

/**
 * Collapse animates the whole vertical box, not just `height`. A container with
 * vertical margin or padding would otherwise go on reserving that space for the
 * entire animation and leave a visible gap where it used to be.
 *
 * The `height` keyframe is resolved against the element's own `box-sizing` so
 * padding and border are counted exactly once: under `content-box` they sit
 * outside the declared height and have to come off it, under `border-box` they
 * are already inside it.
 */
export function collapseKeyframes(metrics: BoxMetrics, withFade: boolean): Keyframe[] {
  const inner =
    metrics.boxSizing === 'border-box'
      ? metrics.outerHeight
      : metrics.outerHeight -
        px(metrics.paddingTop) -
        px(metrics.paddingBottom) -
        px(metrics.borderTopWidth) -
        px(metrics.borderBottomWidth)

  const hidden: Keyframe = {
    height: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
    marginTop: '0px',
    marginBottom: '0px',
    borderTopWidth: '0px',
    borderBottomWidth: '0px',
  }
  const shown: Keyframe = {
    height: `${Math.max(inner, 0)}px`,
    paddingTop: metrics.paddingTop,
    paddingBottom: metrics.paddingBottom,
    marginTop: metrics.marginTop,
    marginBottom: metrics.marginBottom,
    borderTopWidth: metrics.borderTopWidth,
    borderBottomWidth: metrics.borderBottomWidth,
  }
  if (withFade) {
    hidden.opacity = 0
    shown.opacity = 1
  }
  return [hidden, shown]
}

/** `null` means "no motion" — the caller finishes the transition immediately. */
export function motionKeyframes(
  step: NodeAnimationStep,
  metrics: () => BoxMetrics,
): Keyframe[] | null {
  switch (step.type) {
    case 'none':
      return null
    case 'collapse':
      return collapseKeyframes(metrics(), false)
    case 'collapse-fade':
      return collapseKeyframes(metrics(), true)
    case 'fade':
      return [{ opacity: 0 }, { opacity: 1 }]
    case 'scale':
      return [
        { opacity: 0, transform: `scale(${DEFAULT_SCALE})` },
        { opacity: 1, transform: 'none' },
      ]
    case 'fade-slide':
    default: {
      const { dx, dy } = slideOffset(step)
      return [
        { opacity: 0, transform: `translate(${dx}px, ${dy}px)` },
        { opacity: 1, transform: 'none' },
      ]
    }
  }
}
